// App shell: topbar + sidebar + hash-routed outlet. Light DOM so
// console.css styles everything (docs/FRONTEND.md).

import { html, LitElement, nothing } from "lit";
import { auth } from "../lib/api";
import { currentRoute, navigate, store } from "../lib/store";

const THEME_KEY = "mm-theme";

export class MmApp extends LitElement {
  private unsubscribe: (() => void) | null = null;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    window.addEventListener("hashchange", this.onHashChange);
    store.set({ route: currentRoute() });
    void this.bootstrap();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    window.removeEventListener("hashchange", this.onHashChange);
  }

  private onHashChange = (): void => {
    store.set({ route: currentRoute() });
  };

  private async bootstrap(): Promise<void> {
    try {
      const me = await auth.me();
      store.set({ user: me.user, provider: me.provider, hostedMeetings: me.hosted_meetings });
      if (currentRoute() === "/login") navigate("/");
    } catch {
      navigate("/login");
    }
  }

  private toggleTheme(): void {
    const root = document.documentElement;
    if (root.dataset.theme === "light") {
      delete root.dataset.theme;
      localStorage.setItem(THEME_KEY, "dark");
    } else {
      root.dataset.theme = "light";
      localStorage.setItem(THEME_KEY, "light");
    }
    this.requestUpdate();
  }

  private async logout(): Promise<void> {
    await auth.logout();
    store.set({ user: null });
    navigate("/login");
  }

  private outlet() {
    const route = store.get().route;
    if (route.startsWith("/login")) return html`<mm-login></mm-login>`;
    const join = route.match(/^\/join\/([a-z0-9-]+)/);
    if (join) return html`<mm-join .code=${join[1]}></mm-join>`;
    if (route.startsWith("/admin")) return html`<mm-admin></mm-admin>`;
    const meeting = route.match(/^\/meetings\/(\d+)/);
    if (meeting) {
      return html`<mm-meeting-detail
        .meetingId=${Number(meeting[1])}
        .route=${route}
      ></mm-meeting-detail>`;
    }
    if (route.startsWith("/search")) return html`<mm-search .route=${route}></mm-search>`;
    if (route.startsWith("/import")) return html`<mm-import></mm-import>`;
    if (route.startsWith("/settings")) return html`<mm-settings></mm-settings>`;
    return html`<mm-meeting-list></mm-meeting-list>`;
  }

  override render() {
    const { user, route } = store.get();
    const light = document.documentElement.dataset.theme === "light";
    // Join links work for guests (no account) — standalone page,
    // no shell, no login wall (plan 0002 D14). The admin portal is
    // token-gated, not session-gated (plan 0004).
    if (route.startsWith("/join/") || route.startsWith("/admin")) {
      return html`<main class="shell-content" style="max-width: 56rem; margin: 0 auto">
        ${this.outlet()}
      </main>`;
    }
    if (!user || route.startsWith("/login")) {
      return html`<div class="auth-page">
        <div class="auth-card"><mm-login></mm-login></div>
      </div>`;
    }
    const navItem = (href: string, label: string, active: boolean) =>
      html`<a
        class="sidebar-item ${active ? "active" : ""}"
        href="#${href}"
        >${label}</a
      >`;
    return html`
      <div class="shell">
        <header class="shell-topbar">
          <span class="brand">Meeting Manager</span>
          <div class="shell-meta">
            <span class="identity-chip"><code>${user.email}</code></span>
            <button
              class="theme-toggle"
              aria-pressed=${light ? "true" : "false"}
              @click=${this.toggleTheme}
            >
              ${light ? "Dark mode" : "Light mode"}
            </button>
            <button class="ghost-button" @click=${this.logout}>Sign out</button>
          </div>
        </header>
        <nav class="shell-sidebar">
          <div class="sidebar-section">Workspace</div>
          <div class="sidebar-nav">
            ${navItem("/", "Meetings", route === "/" || route.startsWith("/meetings"))}
            ${navItem("/search", "Search", route.startsWith("/search"))}
            ${navItem("/import", "Import audio", route.startsWith("/import"))}
            ${navItem("/settings", "Settings", route.startsWith("/settings"))}
          </div>
          <div class="sidebar-note">
            <h2>Capture tip</h2>
            <p>
              For meetings in another tab, click “Share tab audio” in the
              recorder and tick the audio checkbox in the picker.
            </p>
          </div>
        </nav>
        <main class="shell-content">${this.outlet()}</main>
      </div>
      ${nothing}
    `;
  }
}

customElements.define("mm-app", MmApp);
